import type {
  CSSProperties,
  HTMLAttributeAnchorTarget,
  MouseEvent,
  ReactNode,
} from 'react';
import { useMemo } from 'react';
import { Link, NavLink, useLocation } from 'react-router';

import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { getDataAndAriaProps } from '#/utils/getDataAndAriaProps';
import { getUrlWithoutOrigin } from '#/utils/url/getUrlWithoutOrigin';
import { isUrlWithProtocol } from '#/utils/url/isUrlWithProtocol';
import { useVersionUpdateRequired } from '#/zustand-stores/appVersionStore';

type Props = {
  children?: ReactNode;
  id?: string;
  className?: string;
  navLink?: boolean;
  to: string;
  state?: Record<string, unknown>;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  tabIndex?: number;
  target?: HTMLAttributeAnchorTarget;
  title?: string;
  style?: CSSProperties;
  qaId?: string;
};

export function VersionedRouterLink({
  children,
  id,
  className,
  navLink,
  to,
  onClick,
  state,
  tabIndex,
  target,
  title,
  style,
  qaId,
  ...dataAndAriaProps
}: Props) {
  const location = useLocation();

  // Multi-router routing: ensure routing to external routes is handled by the browser
  const isExternalRoute = useMemo(() => {
    const wasInStaffApp =
      location.pathname.startsWith('/cms') ||
      location.pathname.startsWith('/staff');

    const toStaffApp = to.startsWith('/cms') || to.startsWith('/staff');

    return wasInStaffApp !== toStaffApp;
  }, [location, to]);

  const { versionUpdateRequired } = useVersionUpdateRequired();

  const normalizedTo = useMemo(
    () => (isUrlWithProtocol(to) ? getUrlWithoutOrigin(to) : to),
    [to],
  );

  const willReloadForVersionUpdate =
    versionUpdateRequired && !state && (!target || target === '_self');

  const trackUpdatingAppVersion = () => {
    trackClicked('Updating App Version', { url: normalizedTo });
  };

  const Component = navLink ? NavLink : Link;

  return (
    <Component
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...getDataAndAriaProps(dataAndAriaProps)}
      id={id}
      className={className}
      to={normalizedTo}
      onClick={willReloadForVersionUpdate ? trackUpdatingAppVersion : onClick}
      state={state}
      discover="none"
      reloadDocument={isExternalRoute || willReloadForVersionUpdate}
      target={target}
      tabIndex={tabIndex}
      title={title}
      style={style}
      data-qa-id={qaId}
    >
      {children}
    </Component>
  );
}
